import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Divider, type FormItemProps, Space } from 'antd';
import { FieldArray, useField } from 'formik';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    type KycPresetSettings,
    type KycFieldSettings,
    type ConditionSettings,
    type ConditionType,
} from '../../../../api/types';
import FormItem from '../../../../components/fields/FormItem';
import SelectField from '../../../../components/fields/SelectField';
import useConditionTypesOptions from '../../../../utilities/useConditionTypeOptions';
import ConditionsCard from './ConditionsCard';

const StyledButton = styled.div`
    text-align: center;
`;

const StyledLogicCondition = styled.div`
    padding: 10px;
    border: 1px dashed gray;
    border-radius: 3px;
    gap: 5px;
`;

export type LogicConditionFieldArrayProps = {
    name: string;
    label?: string;
    labelForAdd?: string;
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'required' | 'children'>;
    limit?: number;
};

export type FormValues = KycPresetSettings & {
    fields: KycFieldSettings[];
    conditionSettings: ConditionSettings[];
};

const renderConditionType = (conditionType: ConditionType, prefix: string) => (
    <ConditionsCard conditionType={conditionType} prefix={prefix} />
);

const LogicConditionFieldArray = ({
    name,
    label,
    labelForAdd,
    itemProps,
    limit,
    ...props
}: LogicConditionFieldArrayProps) => {
    const { t } = useTranslation('consentsAndDeclarations');

    const [field, meta] = useField({ name });
    const { value } = meta;

    // if the user choose `OR` conditions, it shouldn't limit into same condition type only
    const options = useConditionTypesOptions(false).conditionTypeOptions;

    const renderDivider = () => <Divider>{t('consentsAndDeclarations:logicConditionDivider')}</Divider>;

    return (
        <FormItem {...itemProps} label={label} meta={meta} name={name}>
            <FieldArray
                {...field}
                name={name}
                render={arrayHelpers => {
                    const addButton = (
                        <StyledButton>
                            <Button
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    arrayHelpers.push('');
                                }}
                                type="link"
                            >
                                {labelForAdd}
                            </Button>
                        </StyledButton>
                    );

                    return (
                        <Space direction="vertical" style={{ width: '100%' }}>
                            {value?.map((key: string, index: number) => (
                                <>
                                    <StyledLogicCondition>
                                        <Col>
                                            <SelectField
                                                {...t('consentsAndDeclarations:fields.conditionType', {
                                                    returnObjects: true,
                                                })}
                                                key={`${name}.${index.toString()}`}
                                                name={`${name}.${index}.type`}
                                                options={options}
                                                showSearch
                                            />
                                        </Col>
                                        {renderConditionType(value[index].type, `${name}.${index}`)}
                                        <Col>
                                            <StyledButton>
                                                <Button
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => arrayHelpers.remove(index)}
                                                    style={{ color: 'red' }}
                                                    type="link"
                                                >
                                                    {t('consentsAndDeclarations:removeLogicCondition')}
                                                </Button>
                                            </StyledButton>
                                        </Col>
                                    </StyledLogicCondition>
                                    {renderDivider()}
                                </>
                            ))}
                            {addButton}
                        </Space>
                    );
                }}
            />
        </FormItem>
    );
};

export default LogicConditionFieldArray;
